"use client";

import { Button } from "@/components/ui/Button";
import { P } from "@/components/ui/Heading";
import { Link } from "@/i18n/routing";
import { gTagEvents } from "@/lib/gtag";
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  YoutubeIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useCallback } from "react";
import { LanguageSelect } from "../LanguageSelect";

const SAKANI_LINKS = [
  {
    key: "about-us",
    link: "/about-us",
  },
  {
    key: "blogs",
    link: "/blogs",
  },
  {
    key: "contact-us",
    link: "https://forms.gle/kYPiPMRqi7k96V1D6",
  },
];

const PARTNERSHIP_LINKS = [
  {
    key: "agent-hub",
    link: "/agent-hub",
  },
];

export const Footer = () => {
  const t = useTranslations("shared.navigation.footer");

  const handleOutboundClick = useCallback((target: string) => {
    gTagEvents.outboundClick(target);
  }, []);

  return (
    <section className="border-t-primary border border-b-0 bg-[#EFF1F5] mt-10 sm:mt-[120px]">
      <div className="w-full py-8 px-3 sm:px-20 space-y-12">
        <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 ">
          <div className="h-6 w-40 relative">
            <Link href={"/"}>
              <Image
                fill
                src="a242c3e6-dd5a-4029-9d8e-cecaddfc9c00"
                alt="logo"
                className="cursor-pointer"
              />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-6">
              <P className="font-semibold">{t("sakani.title")}</P>
              <div className="flex flex-col">
                {SAKANI_LINKS.map((link) => (
                  <Link className="font-light" href={link.link} key={link.key}>
                    {t(`sakani.links.${link.key}`)}
                  </Link>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <P className="font-semibold">{t("partnership.title")}</P>
              <div>
                {PARTNERSHIP_LINKS.map((link) => (
                  <Link className="font-light" href={link.link} key={link.key}>
                    {t(`partnership.links.${link.key}`)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-4 max-w-52">
            <Link
              href={"https://www.facebook.com/sakanihomes/"}
              target="_blank"
              onClick={() => handleOutboundClick("facebook")}
            >
              <Button className="rounded-full h-10 w-10">
                <FacebookIcon className="!size-5" />
              </Button>
            </Link>

            <Link
              href={"https://www.instagram.com/sakanihomes/"}
              target="_blank"
              onClick={() => handleOutboundClick("instagram")}
            >
              <Button className="rounded-full h-10 w-10">
                <InstagramIcon className="!size-5" />
              </Button>
            </Link>

            <Link
              href={"https://www.linkedin.com/company/sakanihomes"}
              target="_blank"
              onClick={() => handleOutboundClick("linkedin")}
            >
              <Button className="rounded-full h-10 w-10">
                <LinkedinIcon className="!size-5" />
              </Button>
            </Link>

            <Link
              href={"https://www.youtube.com/@SakaniHomes"}
              onClick={() => handleOutboundClick("youtube")}
            >
              <Button className="rounded-full h-10 w-10">
                <YoutubeIcon className="!size-5" />
              </Button>
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <P className="!text-gray-500 text-xs self-center">{t("copyright")}</P>
          <div className="flex items-center w-full justify-evenly">
            <LanguageSelect />

            <P>|</P>
            <Link href="/terms-and-conditions" className="text-sm">
              {t("terms-and-conditions")}
            </Link>
            <P>|</P>
            <Link href="/privacy-policy" className="text-sm">
              {t("privacy-policy")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
